import React, { FC } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { Circle } from '@mui/icons-material';
import { BaseStyleEnums, ShortCodeEnums, CoreCommonMessages } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { GAME_TIME } from '~/core/dateTimeFormats';
import { CountryCode, DraftState, PageName } from '~/core/enums';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import TvBroadcast from '~/views/components/tvBroadcast/TvBroadcast';
import DraftLinks from '~/widgets/draftMiniTracker/components/draftLinks/DraftLinks';

import { Draft } from '../../types';

import {
    DraftImage,
    DraftTime,
    FinalIndicator,
    HeaderWrapper,
    LiveIndicator,
    MediumGapContainer,
    SemiMediumGapContainer,
} from './styles';

type Props = {
    draft: Draft;
    countryCode: CountryCode;
    language: ShortCodeEnums.Language;
    showDraftCentralLink: boolean;
    showFullTrackerLink: boolean;
};

const Header: FC<Props> = ({ draft, countryCode, language, showDraftCentralLink, showFullTrackerLink }) => {
    const screenSize = useSizeWithThrottle();

    const isMediumOrLargerScreen = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const draftImage = language === ShortCodeEnums.Language.French ? draft.logoFrUrl : draft.logoUrl;

    return (
        <HeaderWrapper>
            <DraftImage src={draftImage} />
            <MediumGapContainer $expand>
                {draft.state === DraftState.OVER && isMediumOrLargerScreen ? (
                    <FinalIndicator>
                        <FormattedMessage
                            {...CoreCommonMessages.gameStateDescriptions.long[ShortCodeEnums.GameState.official]}
                        />
                    </FinalIndicator>
                ) : (
                    draft.state !== DraftState.OVER && (
                        <>
                            <FormattedMessage
                                description="Indicates which day of the draft we are currently on - eg Day 1"
                                defaultMessage="Day {day}"
                                values={{
                                    day: [DraftState.OVER_DAY_1, DraftState.LIVE_DAY_2].includes(draft.state) ? 2 : 1,
                                }}
                            />
                            &nbsp;-&nbsp;
                            {DateTime.fromISO(draft.currentDraftDate).toLocaleString({
                                month: 'long',
                                day: 'numeric',
                            })}
                            {[DraftState.LIVE_DAY_1, DraftState.LIVE_DAY_2].includes(draft.state) ? (
                                <LiveIndicator>
                                    <Circle />
                                    &nbsp;
                                    <FormattedMessage
                                        {...CoreCommonMessages.gameStateDescriptions.long[
                                            ShortCodeEnums.GameState.live
                                        ]}
                                    />
                                </LiveIndicator>
                            ) : (
                                <DraftTime>
                                    {DateTime.fromISO(draft.broadcastStartTimeUTC).toLocaleString(GAME_TIME)}
                                </DraftTime>
                            )}
                        </>
                    )
                )}
            </MediumGapContainer>
            <MediumGapContainer>
                {draft.tvBroadcasts && draft.state !== DraftState.OVER && (
                    <>
                        {isMediumOrLargerScreen && <FormattedMessage {...CommonMessages.misc.watchOn} />}
                        <TvBroadcast
                            location={countryCode}
                            pageName={PageName.Widget}
                            tvBroadcasts={draft.tvBroadcasts}
                        />
                    </>
                )}
                {draft.state === DraftState.OVER && !isMediumOrLargerScreen && (
                    <FinalIndicator>
                        <FormattedMessage
                            {...CoreCommonMessages.gameStateDescriptions.long[ShortCodeEnums.GameState.official]}
                        />
                    </FinalIndicator>
                )}
            </MediumGapContainer>
            {isMediumOrLargerScreen && (
                <SemiMediumGapContainer>
                    <DraftLinks
                        language={language}
                        showDraftCentralLink={showDraftCentralLink}
                        showFullTrackerLink={showFullTrackerLink}
                    />
                </SemiMediumGapContainer>
            )}
        </HeaderWrapper>
    );
};

export default Header;
